/**
 * The tool state machine (§9.2): a pure `(machine, event, config) → result`.
 *
 * Order of resolution for a pointer event:
 *   1. a pinch (two or more pointers down) — viewport only;
 *   2. a pan (`Space` held, or the middle button) — viewport only;
 *   3. the active tool's handler.
 *
 * Keyboard events that belong to *no* tool (`Esc`, `Delete`, tool letters)
 * are handled here so every tool inherits them. Nothing in this file touches
 * React, the store or the DOM, which is why the whole interaction layer can
 * be driven from a test with synthetic events.
 */
import { anchorTool } from './anchor'
import { bandTool } from './band'
import { horizonTool } from './horizon'
import { layerTool } from './layer'
import { magnifierTool } from './magnifier'
import { reducePan, type PanContext } from './pan'
import { pathTool } from './path'
import { reducePinch } from './pinch'
import { polygonTool } from './polygon'
import { portalTool } from './portal'
import { rectTool } from './rect'
import { selectTool } from './select'
import {
  DEFAULT_TOOL_CONFIG,
  IDLE,
  none,
  type ToolConfig,
  type ToolEffect,
  type ToolEvent,
  type ToolHandler,
  type ToolId,
  type ToolMachine,
  type ToolResult,
} from './types'

export const TOOLS: readonly ToolHandler[] = [
  selectTool,
  polygonTool,
  bandTool,
  rectTool,
  horizonTool,
  anchorTool,
  portalTool,
  pathTool,
  layerTool,
  magnifierTool,
] as const

export const TOOL_BY_ID: Readonly<Record<ToolId, ToolHandler>> = Object.fromEntries(TOOLS.map((t) => [t.id, t])) as Record<ToolId, ToolHandler>

/** `event.key` (lower-cased) → tool id, for the rail and the shortcut sheet. */
export const TOOL_BY_SHORTCUT: Readonly<Record<string, ToolId>> = Object.fromEntries(TOOLS.map((t) => [t.shortcut, t.id]))

export interface ReduceContext extends PanContext {
  config: ToolConfig
  /** Pointers currently down, by `pointerId`, in client pixels. */
  pointers: Map<number, { x: number; y: number }>
}

export const DEFAULT_CONTEXT: ReduceContext = {
  config: DEFAULT_TOOL_CONFIG,
  viewport: { k: 1, tx: 0, ty: 0 },
  armed: false,
  pointers: new Map(),
}

/** States that own an open undo gesture and must cancel it when interrupted. */
function holdsGesture(machine: ToolMachine): boolean {
  switch (machine.state.t) {
    case 'vertex.dragging':
    case 'feature.dragging':
    case 'horizon.dragging':
    case 'layer.baseline':
      return true
    default:
      return false
  }
}

/** States the user can walk away from with `Esc` without losing anything saved. */
function isDrawing(machine: ToolMachine): boolean {
  switch (machine.state.t) {
    case 'polygon.drawing':
    case 'path.drawing':
    case 'anchor.placing':
    case 'portal.linking':
    case 'band.dragging':
    case 'rect.dragging':
    case 'marquee':
      return true
    default:
      return false
  }
}

export function reduce(machine: ToolMachine, ev: ToolEvent, ctx: ReduceContext = DEFAULT_CONTEXT): ToolResult {
  // --- global events ------------------------------------------------------
  if (ev.t === 'setTool') {
    const effects: ToolEffect[] = holdsGesture(machine) ? [{ k: 'cancelGesture' }] : []
    return { machine: { tool: ev.tool, state: { t: 'idle' } }, effects }
  }

  if (ev.t === 'spaceDown' || ev.t === 'spaceUp') {
    const pan = reducePan(machine, ev, ctx)
    return pan ?? none(machine)
  }

  if (ev.t === 'cancel') {
    if (holdsGesture(machine)) return { machine: { tool: machine.tool, state: { t: 'idle' } }, effects: [{ k: 'cancelGesture' }] }
    return { machine: { tool: machine.tool, state: { t: 'idle' } }, effects: [] }
  }

  // --- viewport gestures come first ---------------------------------------
  if (ev.t === 'pointerdown' || ev.t === 'pointermove' || ev.t === 'pointerup' || ev.t === 'pointercancel') {
    const pinch = reducePinch(machine, ev, ctx, ctx.pointers)
    if (pinch) return pinch
    const pan = reducePan(machine, ev, ctx)
    if (pan) return pan
  }

  // --- keyboard that every tool shares ------------------------------------
  if (ev.t === 'key') {
    if (ev.key === 'Escape') {
      if (holdsGesture(machine)) return { machine: { tool: machine.tool, state: { t: 'idle' } }, effects: [{ k: 'cancelGesture' }] }
      if (isDrawing(machine)) return { machine: { tool: machine.tool, state: { t: 'idle' } }, effects: [] }
      // §9.3: Esc cascades — cancel → clear selection → (the layout closes the panel).
      const toolResult = TOOL_BY_ID[machine.tool].reduce(machine, ev, ctx.config)
      if (toolResult) return toolResult
      return { machine: { tool: machine.tool, state: { t: 'idle' } }, effects: [{ k: 'clearSelection' }] }
    }

    // A bare tool letter switches tools, but only while nothing is in flight:
    // `B` must stay available as "Backspace-adjacent" inside a polygon.
    if (!ev.mod && !ev.shift && machine.state.t === 'idle') {
      const next = TOOL_BY_SHORTCUT[ev.key.toLowerCase()]
      if (next && next !== machine.tool) return { machine: { tool: next, state: { t: 'idle' } }, effects: [] }
    }
  }

  // --- the active tool ----------------------------------------------------
  const handler = TOOL_BY_ID[machine.tool]
  const result = handler.reduce(machine, ev, ctx.config)
  return result ?? none(machine)
}

/**
 * Drive a whole list of events through the machine — the shape the tests use
 * and a convenient way to replay a recorded gesture.
 */
export function reduceAll(start: ToolMachine, events: readonly ToolEvent[], ctx: ReduceContext = DEFAULT_CONTEXT): { machine: ToolMachine; effects: ToolEffect[] } {
  let machine = start
  const effects: ToolEffect[] = []
  for (const ev of events) {
    const r = reduce(machine, ev, ctx)
    machine = r.machine
    effects.push(...r.effects)
  }
  return { machine, effects }
}

export { IDLE }
export type { ToolConfig, ToolEffect, ToolEvent, ToolHandler, ToolId, ToolMachine, ToolResult }
